import * as React from 'react'
import { FC, useCallback } from 'react'
import { Theme, useTheme } from '@mui/material/styles'

import { MoreButton, SalesFunnelIcon, toast } from '@sales-center/components'
import { ISalesFunnel } from '@user/store/salesFunnel/typings'
import Card from '@mui/material/Card'
import FormControlLabel, {
  formControlLabelClasses,
} from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Box from '@mui/material/Box'

type Props = {
  salesFunnel: ISalesFunnel
  deleteFunnel: (id: string) => void
  setDefault: (id: string) => void
  editFunnel: (id: string) => void
  duplicateFunnel: (id: string) => void
}

const getStyles = (theme: Theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2.1),
  },

  card: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },

  moreButton: {
    marginLeft: 'auto',
    marginRight: theme.spacing(0.5),
  },

  funnelIcon: {
    margin: `${theme.spacing(1.2)} ${theme.spacing(2.3)}`,
    flexShrink: 0,
  },

  funnelDetail: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  funnelName: {
    fontSize: '16px',
    fontFamily: 'Source Sans Pro Bold',
  },

  recordCount: {
    fontSize: '14px',
  },

  checkbox: {
    margin: `0 ${theme.spacing(2.1)} 0 auto`,
  },

  checkboxLabel: {
    fontSize: '12px',
  },

  actions: {
    display: 'flex',
    marginLeft: 'auto',
    flexDirection: 'column',
    flexShrink: 0,
  },
})
export const SalesFunnelCard: FC<Props> = (props) => {
  const {
    salesFunnel: { id, name, isDefault, recordCount },
    deleteFunnel,
    setDefault,
    editFunnel,
    duplicateFunnel,
  } = props

  const onDeleteFunnel = useCallback(() => {
    if (isDefault) {
      toast.error('Cannot delete the default sales funnel')
    } else if (recordCount > 0) {
      toast.error('Cannot delete a sales funnel with records')
    } else deleteFunnel(id)
  }, [deleteFunnel, id, isDefault, recordCount])

  const onEditFunnel = useCallback(() => {
    editFunnel(id)
  }, [editFunnel, id])

  const onDuplicateFunnel = useCallback(() => {
    duplicateFunnel(id)
  }, [duplicateFunnel, id])

  const onChangeDefault = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) setDefault(id)
    },
    [id, setDefault],
  )

  const styles = getStyles(useTheme())

  return (
    <Box sx={{ ...styles.row }}>
      <Card
        sx={{ ...styles.card }}
        elevation={2}
        square
        data-test-id={`salesFunnelCard${id}`}
      >
        <SalesFunnelIcon style={styles.funnelIcon} />
        <Box sx={{ ...styles.funnelDetail }}>
          <span style={styles.funnelName}>{name}</span>
          <br />
          <span style={styles.recordCount}>
            {recordCount} {recordCount === 1 ? 'Record' : 'Records'}
          </span>
        </Box>

        <Box sx={{ ...styles.actions }}>
          <MoreButton
            dataTestId={`salesFunnelMoreBtn-${id}`}
            style={{ ...styles.moreButton }}
            options={[
              {
                label: 'Edit',
                onClick: onEditFunnel,
                dataTestId: 'salesFunnelMoreEditBtn',
              },
              {
                label: 'Delete',
                onClick: onDeleteFunnel,
                dataTestId: 'salesFunnelMoreDeleteBtn',
              },
              {
                label: 'Duplicate',
                onClick: onDuplicateFunnel,
                dataTestId: 'salesFunnelMoreDuplicateBtn',
              },
            ]}
          />
          <FormControlLabel
            sx={{
              [`& .${formControlLabelClasses.label}`]: styles.checkboxLabel,
              ...styles.checkbox,
            }}
            label="Mark as Default Sales Funnel"
            control={
              <Checkbox
                color="primary"
                checked={isDefault}
                onChange={onChangeDefault}
              />
            }
          />
        </Box>
      </Card>
    </Box>
  )
}
